import { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, CheckCircle2, Circle, Clock, AlertTriangle, X, Calendar,
  User, ChevronDown, Filter, Search, Flag, Link2, ArrowUpDown, RefreshCw, Star, Globe,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import {
  useStore, type Task, type TaskPriority, type TaskStatus,
  PRIORITY_COLORS, TASK_STATUS_COLORS,
} from '../store/useStore';
import { googleEcosystem } from '../lib/google-ecosystem';
import { GoogleCalendarService } from '../lib/google-calendar';

const PRIORITY_ORDER: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'todo', label: 'To Do' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const inputClass = 'w-full px-4 py-3 text-sm rounded-2xl border transition-all outline-none focus:ring-2';
const inputStyle = {
  background: 'var(--t-surface-subtle)',
  borderColor: 'var(--t-border)',
  color: 'var(--t-text)',
  '--tw-ring-color': 'var(--t-primary-dim)',
  fontFamily: 'Inter, sans-serif'
};

const normalizeTitle = (title: string) => {
  return (title || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
};

export default function Tasks() {
  const [searchParams] = useSearchParams();

  const taskId = searchParams.get('id');

  const { 
    tasks: allTasks, team, leads, addTask, updateTask, deleteTask, completeTask,
    favoriteTaskView, setFavoriteTaskView, starredTasks, toggleStarredTask,
    currentUser, googleTasksAutoImport, googleTasksImported, setGoogleTasksImported
  } = useStore();

  // Fix 6: Team privacy - only show tasks assigned to or created by current user
  const tasks = useMemo(() => {
    if (!currentUser?.id) return allTasks;
    return allTasks.filter(t => 
      t.assignedTo === currentUser.id || 
      t.createdBy === currentUser.id ||
      !t.assignedTo // Show unassigned tasks to everyone
    );
  }, [allTasks, currentUser?.id]);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'all'>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Load initial sort from localStorage
  const [sortField, setSortField] = useState<'title' | 'status' | 'due' | 'priority' | 'assignee'>(() => {
    return (localStorage.getItem('tasks_sort_field') as any) || 'priority';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    return (localStorage.getItem('tasks_sort_direction') as any) || 'asc';
  });

  useEffect(() => {
    localStorage.setItem('tasks_sort_field', sortField);
  }, [sortField]);

  useEffect(() => {
    localStorage.setItem('tasks_sort_direction', sortDirection);
  }, [sortDirection]);

  const handleSortClick = (field: 'title' | 'status' | 'due' | 'priority' | 'assignee') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'title' | 'status' | 'due' | 'priority' | 'assignee') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUp size={12} className="inline ml-1" /> : <ArrowDown size={12} className="inline ml-1" />;
  };
  const [viewMode, setViewMode] = useState<'list' | 'board'>(favoriteTaskView);
  const [showFilters, setShowFilters] = useState(false);
  const [syncingTasks, setSyncingTasks] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [syncConfirm, setSyncConfirm] = useState<{
    type: 'first-login' | 'push-confirm';
    unlinkedCount: number;
    oldCount: number;
    onChoose: (choice: 'all' | 'new-only' | 'none' | 'cancel' | 'import-yes' | 'import-no') => void;
  } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (taskId) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSearch(task.title);
      }
    }
  }, [taskId, tasks]);

  const emptyForm = (): Omit<Task, 'id' | 'createdAt' | 'completedAt'> => ({
    title: '', description: '', assignedTo: team[0]?.id || '',
    dueDate: new Date(Date.now() + 86400000).toISOString(),
    priority: 'medium', status: 'todo', createdBy: team[0]?.id || '',
  });

  const [form, setForm] = useState(emptyForm());

  const getMemberName = (id: string) => team.find(m => m.id === id)?.name || 'Unassigned';
  const getMemberAvatar = (id: string) => team.find(m => m.id === id)?.avatar || '?';
  const getLeadName = (id?: string) => id ? leads.find(l => l.id === id)?.name : undefined;

  const filtered = useMemo(() => {
    let result = [...tasks];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(t =>
        (t.title || '').toLowerCase().includes(s) ||
        (t.description || '').toLowerCase().includes(s) ||
        (getMemberName(t.assignedTo) || '').toLowerCase().includes(s)
      );
    }
    if (filterStatus !== 'all') result = result.filter(t => t.status === filterStatus);
    if (filterPriority !== 'all') result = result.filter(t => t.priority === filterPriority);
    if (filterAssignee !== 'all') result = result.filter(t => t.assignedTo === filterAssignee);

    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'priority') {
        const aOrd = PRIORITY_ORDER[a.priority] !== undefined ? PRIORITY_ORDER[a.priority] : 2;
        const bOrd = PRIORITY_ORDER[b.priority] !== undefined ? PRIORITY_ORDER[b.priority] : 2;
        comparison = aOrd - bOrd;
      } else if (sortField === 'due') {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        comparison = aTime - bTime;
      } else if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '');
      } else if (sortField === 'status') {
        comparison = (a.status || '').localeCompare(b.status || '');
      } else if (sortField === 'assignee') {
        comparison = (getMemberName(a.assignedTo) || '').localeCompare(getMemberName(b.assignedTo) || '');
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [tasks, search, filterStatus, filterPriority, filterAssignee, sortField, sortDirection]);

  const startEdit = (task: Task) => {
    setEditingId(task.id);
    setShowAdd(false);
    setForm({
      title: task.title, description: task.description,
      assignedTo: task.assignedTo, dueDate: task.dueDate,
      priority: task.priority, status: task.status,
      createdBy: task.createdBy, leadId: task.leadId,
    });
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    if (editingId) {
      updateTask(editingId, form);
      setEditingId(null);
    } else {
      addTask(form);
      setShowAdd(false);
    }
    setForm(emptyForm());
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;
    
    if (status === 'done') {
      completeTask(taskId);
    } else {
      updateTask(taskId, { status, completedAt: null });
    }
    toast.success(`Task moved to ${status === 'todo' ? 'To Do' : status === 'in-progress' ? 'In Progress' : 'Done'}`);
  };

  const handleSyncTasks = async (overrideImportCheck = false, isAuto = false) => {
    if (syncingTasks) return;
    const { currentUser } = useStore.getState();
    if (!currentUser?.id) {
      setSyncingTasks(false);
      return;
    }
    setSyncingTasks(true);
    try {
      const data = await googleEcosystem.getTasksLists(currentUser.id);
      const lists = data.items || [];
      
      // Filter matching list names
      const targetList = lists.find((l: any) => l.title === 'WholeScale OS') || 
                         lists.find((l: any) => l.title === 'WholeScale') || 
                         lists.find((l: any) => l.title === 'CRM Tasks') || 
                         lists.find((l: any) => l.title === 'My Tasks') || 
                         lists[0];

      if (!targetList) {
        throw new Error('No task lists found on Google');
      }

      // Check first login / first import
      if (!googleTasksImported && !overrideImportCheck) {
        setSyncConfirm({
          type: 'first-login',
          unlinkedCount: 0,
          oldCount: 0,
          onChoose: async (choice) => {
            try {
              setSyncConfirm(null);
              if (choice === 'import-yes') {
                setGoogleTasksImported(true);
                await runSyncTasksFlow(targetList, 'all');
              } else if (choice === 'import-no') {
                setGoogleTasksImported(true);
                await runSyncTasksFlow(targetList, 'none');
              } else {
                setSyncingTasks(false);
              }
            } catch (err) {
              console.error(err);
              setSyncingTasks(false);
            }
          }
        });
        return;
      }

      // Check if we need to push tasks
      const currentTasks = useStore.getState().tasks;
      const unlinkedTasks = currentTasks.filter(t => !t.googleTaskId && (t.createdBy === currentUser.id || t.assignedTo === currentUser.id));
      
      if (unlinkedTasks.length > 0) {
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        const oldUnlinked = unlinkedTasks.filter(t => t.createdAt && (now - new Date(t.createdAt).getTime()) > oneDayMs);
        
        setSyncConfirm({
          type: 'push-confirm',
          unlinkedCount: unlinkedTasks.length,
          oldCount: oldUnlinked.length,
          onChoose: async (choice) => {
            try {
              setSyncConfirm(null);
              if (choice === 'cancel') {
                setSyncingTasks(false);
                return;
              }
              await runSyncTasksFlow(targetList, choice as 'all' | 'new-only' | 'none');
            } catch (err) {
              console.error(err);
              setSyncingTasks(false);
            }
          }
        });
        return;
      }

      await runSyncTasksFlow(targetList, 'all');
    } catch (err: any) {
      console.error(err);
      if (isAuto) {
        console.log('[Auto-Sync] Silently skipped auto-sync:', err.message);
        setSyncingTasks(false);
        return;
      }
      if (err.message?.includes('403')) {
        setIsAuthError(true);
      }
      toast.error('Failed to sync tasks: ' + (err.message || 'Unknown error'));
      setSyncingTasks(false);
    }
  };

  const runSyncTasksFlow = async (targetList: any, pushChoice: 'all' | 'new-only' | 'none') => {
    const { currentUser } = useStore.getState();
    if (!currentUser?.id) {
      setSyncingTasks(false);
      return;
    }
    setSyncingTasks(true);
    try {
      const tasksData = await googleEcosystem.getTasks(currentUser.id, targetList.id);
      const googleTasks = (tasksData.items || []).filter((gt: any) => !gt.deleted && gt.status !== 'deleted');
      const googleTaskIds = new Set(googleTasks.map((gt: any) => gt.id));

      let newFromGoogle = 0;
      let deletedLocally = 0;

      if (pushChoice !== 'none' || googleTasksImported) {
        googleTasks.forEach((gt: any) => {
          const currentTasks = useStore.getState().tasks;
          const existing = currentTasks.find(t => 
            t.googleTaskId === gt.id || 
            normalizeTitle(t.title) === normalizeTitle(gt.title)
          );
          
          if (!existing) {
            addTask({
              title: gt.title,
              description: gt.notes || '',
              assignedTo: currentUser.id,
              dueDate: gt.due ? new Date(gt.due).toISOString() : new Date(Date.now() + 86400000).toISOString(),
              priority: 'medium',
              status: gt.status === 'completed' ? 'done' : 'todo',
              createdBy: currentUser.id,
              googleTaskId: gt.id
            } as any);
            newFromGoogle++;
          } else {
            if (!existing.googleTaskId) {
              updateTask(existing.id, { googleTaskId: gt.id } as any);
            }
            const expectedStatus = gt.status === 'completed' ? 'done' : existing.status;
            if (existing.status !== expectedStatus) {
              updateTask(existing.id, { status: expectedStatus } as any);
            }
          }
        });
      }

      let pushedToGoogle = 0;
      const freshTasks = useStore.getState().tasks;
      for (const localTask of freshTasks) {
        if (localTask.createdBy !== currentUser.id && localTask.assignedTo !== currentUser.id && localTask.assignedTo) {
          continue;
        }

        if (localTask.googleTaskId) {
          if (!googleTaskIds.has(localTask.googleTaskId)) {
            deleteTask(localTask.id);
            deletedLocally++;
          } else {
            // Task has a googleTaskId and exists on Google Tasks: check for updates
            const gt = googleTasks.find((g: any) => g.id === localTask.googleTaskId);
            if (gt) {
              const localStatus = localTask.status === 'done' ? 'completed' : 'needsAction';
              const titleChanged = localTask.title !== gt.title;
              const statusChanged = localStatus !== gt.status;
              const notesChanged = (localTask.description || '') !== (gt.notes || '');

              if (titleChanged || statusChanged || notesChanged) {
                try {
                  await googleEcosystem.updateGoogleTask(currentUser.id, localTask.googleTaskId, {
                    title: localTask.title,
                    status: localTask.status,
                    notes: localTask.description || undefined
                  }, targetList.id);
                } catch (updateErr) {
                  console.warn('[Tasks Sync] Failed to update task on Google:', localTask.title, updateErr);
                }
              }
            }
          }
        } else if (localTask.createdBy === currentUser.id) {
          if (pushChoice === 'none') {
            continue;
          }
          if (pushChoice === 'new-only') {
            const now = Date.now();
            const oneDayMs = 24 * 60 * 60 * 1000;
            const isOld = localTask.createdAt && (now - new Date(localTask.createdAt).getTime()) > oneDayMs;
            if (isOld) continue;
          }

          try {
            const newGt = await googleEcosystem.createTask(currentUser.id, {
              title: localTask.title,
              notes: localTask.description || undefined,
              due: localTask.dueDate
            }, targetList.id);
            updateTask(localTask.id, { googleTaskId: newGt.id } as any);
            pushedToGoogle++;
          } catch (pushErr) {
            console.warn('[Tasks Sync] Failed to push task to Google:', localTask.title, pushErr);
          }
        }
      }

      toast.success(`Synced: ${newFromGoogle} new, ${deletedLocally} deleted, ${pushedToGoogle} pushed`);
      setIsAuthError(false);
    } catch (err: any) {
      console.error(err);
      if (err.message?.includes('403')) {
        setIsAuthError(true);
      }
      toast.error('Failed to sync tasks: ' + (err.message || 'Unknown error'));
    } finally {
      setSyncingTasks(false);
    }
  };

  useEffect(() => {
    if (googleTasksAutoImport) {
      handleSyncTasks(false, true);
    }
  }, []);

  const handleDeleteTask = async (task: Task) => {
    deleteTask(task.id);
    const { currentUser } = useStore.getState();
    if (currentUser?.id && task.googleTaskId) {
      try {
        const data = await googleEcosystem.getTasksLists(currentUser.id);
        const lists = data.items || [];
        const targetList = lists.find((l: any) => l.title === 'WholeScale OS') || 
                           lists.find((l: any) => l.title === 'WholeScale') || 
                           lists.find((l: any) => l.title === 'CRM Tasks') || 
                           lists.find((l: any) => l.title === 'My Tasks') || 
                           lists[0];
        if (targetList) {
          await googleEcosystem.deleteGoogleTask(currentUser.id, task.googleTaskId, targetList.id);
        }
      } catch (err) {
        console.warn('Failed to delete from google tasks', err);
      }
    }
  };

  const handleReconnect = () => {
    const authUrl = GoogleCalendarService.getInstance().getTasksAuthUrl('/tasks');
    window.location.href = authUrl;
  };

  const getDueLabel = (d: string) => {
    const date = parseISO(d);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d');
  };

  const getDueColor = (t: Task) => {
    if (t.status === 'done' || t.status === 'cancelled') return 'var(--t-text-muted)';
    if (!t.dueDate) return 'var(--t-text-muted)';
    const d = parseISO(t.dueDate);
    if (isPast(d)) return 'var(--t-error)';
    if (isToday(d)) return 'var(--t-warning)';
    return 'var(--t-text-muted)';
  };

  const TaskRow = ({ task }: { task: Task }) => {
    const pc = PRIORITY_COLORS[task.priority];
    const sc = TASK_STATUS_COLORS[task.status];
    const leadName = getLeadName(task.leadId);
    const isDone = task.status === 'done';
    const isCancelled = task.status === 'cancelled';

    return (
      <div
        className={`group border rounded-xl p-3 transition-all hover:shadow-xl hover:border-[var(--t-primary)] ${isDone || isCancelled ? 'opacity-60 grayscale-[0.5]' : ''} astral-glass`}
        style={{ 
          background: 'var(--t-surface)', 
          borderColor: 'var(--t-border)'
        }}
      >
        <div className="flex flex-col md:grid md:grid-cols-12 md:gap-4 md:items-center">
          {/* Check and Star */}
          <div className="col-span-1 flex items-center gap-2 mb-2 md:mb-0">
            <button
              onClick={() => isDone ? updateTask(task.id, { status: 'todo', completedAt: null }) : completeTask(task.id)}
              className={`shrink-0 transition-all hover:scale-110 active:scale-90`}
              style={{ color: isDone ? 'var(--t-success)' : 'var(--t-border-focus)' }}
            >
              {isDone ? <CheckCircle2 size={20} /> : <Circle size={20} />}
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); toggleStarredTask(task.id); }}
              className={`transition-colors flex-shrink-0 ${starredTasks.includes(task.id) ? 'text-[var(--t-warning)]' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              <Star size={18} fill={starredTasks.includes(task.id) ? 'currentColor' : 'none'} />
            </button>
          </div>

          {/* Title & Description */}
          <div className="col-span-4 min-w-0 mb-2 md:mb-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`text-xs font-semibold ${isDone ? 'italic line-through text-[var(--t-text-muted)]' : 'text-[var(--t-text)]'}`}>{task.title}</h3>
              {leadName && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold italic" style={{ color: 'var(--t-primary)' }}>
                  <Link2 size={10} />
                  {leadName}
                </span>
              )}
            </div>
            {task.description && (
              <p className="text-[10px] mt-0.5 line-clamp-1" style={{ color: 'var(--t-text-muted)' }}>{task.description}</p>
            )}
          </div>

          {/* Status */}
          <div className="col-span-2 mb-2 md:mb-0">
            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md border uppercase tracking-wider ${sc.text}`} style={{ background: sc.bg, borderColor: 'currentColor' }}>
              {task.status === 'in-progress' ? 'In Progress' : task.status}
            </span>
          </div>

          {/* Due Date */}
          <div className="col-span-2 mb-2 md:mb-0">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold`} style={{ color: getDueColor(task) }}>
              <Calendar size={11} />
              {getDueLabel(task.dueDate)}
              {isPast(parseISO(task.dueDate)) && task.status !== 'done' && task.status !== 'cancelled' && (
                <AlertTriangle size={12} className="animate-bounce" />
              )}
            </span>
          </div>

          {/* Priority */}
          <div className="col-span-1 mb-2 md:mb-0">
            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md border uppercase tracking-wider ${pc.text}`} style={{ background: pc.bg, borderColor: 'currentColor' }}>
              {task.priority}
            </span>
          </div>

          {/* Assignee */}
          <div className="col-span-1 mb-2 md:mb-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold" style={{ color: 'var(--t-text-muted)' }}>
              <div className="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-black text-white shadow-lg animate-pulse"
                style={{ background: 'linear-gradient(135deg, var(--t-primary), #9333ea)' }}
              >
                {getMemberAvatar(task.assignedTo)}
              </div>
              <span className="truncate max-w-[80px]">{getMemberName(task.assignedTo)}</span>
            </span>
          </div>

          {/* Actions */}
          <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!isDone && !isCancelled && (
              <select
                value={task.status}
                onChange={(e) => {
                  const newStatus = e.target.value as TaskStatus;
                  if (newStatus === 'done') completeTask(task.id);
                  else updateTask(task.id, { status: newStatus });
                }}
                className="text-xs border rounded-lg px-2 py-1 outline-none"
                style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
              >
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <button onClick={() => startEdit(task)} className="p-1.5 transition-colors rounded-lg hover:opacity-80" style={{ color: 'var(--t-text-muted)' }}>
              <Flag size={14} />
            </button>
            <button 
              onClick={() => handleDeleteTask(task)} 
              className="p-1.5 transition-colors rounded-lg border border-transparent" 
              style={{ color: 'var(--t-error)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--t-error-dim)';
                e.currentTarget.style.borderColor = 'var(--t-error-border)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Board view grouping
  const boardColumns: { status: TaskStatus; label: string; color: string }[] = [
    { status: 'todo', label: 'To Do', color: 'rgba(var(--t-text-rgb), 0.3)' },
    { status: 'in-progress', label: 'In Progress', color: 'border-[var(--t-primary)]' },
    { status: 'done', label: 'Done', color: 'border-[var(--t-success)]' },
  ];

  return (

    <div className="crm-container flex flex-col h-full overflow-hidden space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-[var(--t-surface)] p-4 rounded-3xl border border-[var(--t-border)] shadow-xl astral-glass shrink-0">
        <div>
          <h1 className="text-2xl font-black italic uppercase tracking-widest" style={{ color: 'var(--t-text)' }}>Tasks</h1>
          <p className="text-xs mt-1 font-medium" style={{ color: 'var(--t-text-muted)' }}>Manage team assignments and to-dos</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <button
            onClick={() => handleSyncTasks()}
            disabled={syncingTasks}
            className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] rounded-xl transition-all border shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50"
            style={{ 
              background: syncingTasks ? 'var(--t-primary)' : 'var(--t-surface-dim)', 
              color: syncingTasks ? 'var(--t-on-primary)' : 'var(--t-text)',
              borderColor: syncingTasks ? 'var(--t-primary)' : 'var(--t-border)',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            <RefreshCw size={12} className={syncingTasks ? 'animate-spin' : ''} />
            Sync
          </button>

          {isAuthError && (
            <button
              onClick={handleReconnect}
              className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] rounded-xl transition-all border shadow-lg hover:scale-105 active:scale-95 animate-pulse"
              style={{ 
                background: 'var(--t-error)', 
                color: 'white',
                borderColor: 'var(--t-error-border)',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <AlertTriangle size={12} />
              Reconnect Google
            </button>
          )}

          <div className="flex bg-[var(--t-background)] rounded-xl p-1 border border-[var(--t-border)] shadow-inner">
            <button
              onClick={() => setViewMode('list')}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all"
              style={viewMode === 'list' ? { 
                background: 'var(--t-primary)', 
                color: 'var(--t-on-primary)',
                boxShadow: '0 4px 12px var(--t-primary-dim)',
                fontFamily: 'Inter, sans-serif'
              } : { color: 'var(--t-text-muted)', fontFamily: 'Inter, sans-serif' }}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('board')}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all"
              style={viewMode === 'board' ? { 
                background: 'var(--t-primary)', 
                color: 'var(--t-on-primary)',
                boxShadow: '0 4px 12px var(--t-primary-dim)',
                fontFamily: 'Inter, sans-serif'
              } : { color: 'var(--t-text-muted)', fontFamily: 'Inter, sans-serif' }}
            >
              Board
            </button>
          </div>

          <button 
            onClick={() => {
              setFavoriteTaskView(viewMode);
              toast.success(`${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} set as default view`);
            }}
            className={`p-2 rounded-xl border transition-all ${favoriteTaskView === viewMode ? 'text-[var(--t-warning)] border-[var(--t-warning)] bg-[var(--t-warning)]/10' : 'text-[var(--t-text-muted)] border-[var(--t-border)] hover:text-white hover:bg-[var(--t-surface)]'}`}
            title="Set current view as default"
          >
            <Star size={16} fill={favoriteTaskView === viewMode ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={() => { setShowAdd(true); setEditingId(null); setForm(emptyForm()); }}
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-[0.15em] rounded-xl transition-all shadow-xl hover:scale-105 active:scale-95 group overflow-hidden relative"
            style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)', fontFamily: 'Inter, sans-serif' }}
          >
            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300 slant-glow" />
            <Plus size={16} /> New Task
          </button>
        </div>
      </div>


      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 shrink-0">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-text-muted)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks..."
            className={`${inputClass} pl-10`}
            style={inputStyle}
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1.5 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all"
          style={showFilters ? { 
            background: 'var(--t-primary-dim)', 
            borderColor: 'var(--t-primary)', 
            color: 'var(--t-primary)' 
          } : { 
            background: 'var(--t-surface)', 
            borderColor: 'var(--t-border)', 
            color: 'var(--t-text-muted)' 
          }}
        >
          <Filter size={14} /> Filters <ChevronDown size={12} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        <button
          onClick={() => {
            const nextFields: Record<typeof sortField, typeof sortField> = {
              priority: 'due',
              due: 'status',
              status: 'title',
              title: 'assignee',
              assignee: 'priority'
            };
            handleSortClick(nextFields[sortField]);
          }}
          className="flex items-center gap-1.5 px-3 py-2.5 text-sm rounded-xl border transition-colors select-none"
          style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
        >
          <ArrowUpDown size={14} /> Sort: {sortField === 'priority' ? 'Priority' : sortField === 'due' ? 'Due Date' : sortField === 'status' ? 'Status' : sortField === 'title' ? 'Title' : 'Assignee'} {sortDirection === 'asc' ? '↑' : '↓'}
        </button>
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 p-4 border rounded-xl shrink-0" style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--t-text-muted)' }}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'all')}
              className="text-xs border rounded-lg px-3 py-2 outline-none"
              style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            >
              <option value="all">All</option>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--t-text-muted)' }}>Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as TaskPriority | 'all')}
              className="text-xs border rounded-lg px-3 py-2 outline-none"
              style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            >
              <option value="all">All</option>
              {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--t-text-muted)' }}>Assigned To</label>
            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="text-xs border rounded-lg px-3 py-2 outline-none"
              style={{ background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            >
              <option value="all">All Members</option>
              {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Add / Edit Form */}
      {(showAdd || editingId) && (
        <div className="border rounded-2xl p-5 space-y-4 shrink-0 overflow-y-auto max-h-[35vh]" style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{editingId ? 'Edit Task' : 'New Task'}</h3>
            <button onClick={() => { setShowAdd(false); setEditingId(null); }} style={{ color: 'var(--t-text-muted)' }} className="hover:text-white"><X size={16} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Title *</label>
              <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inputClass} style={inputStyle} placeholder="Task title..." />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Description</label>
              <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={`${inputClass} h-20 resize-none`} style={inputStyle} placeholder="Task details..." />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Assign To</label>
              <select value={form.assignedTo} onChange={(e) => setForm(f => ({ ...f, assignedTo: e.target.value }))} className={inputClass} style={inputStyle}>
                {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Due Date</label>
              <input type="datetime-local" value={form.dueDate.slice(0, 16)} onChange={(e) => setForm(f => ({ ...f, dueDate: new Date(e.target.value).toISOString() }))} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Priority</label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map(o => (
                  <button key={o.value} onClick={() => setForm(f => ({ ...f, priority: o.value }))}
                    className={`flex-1 py-2 text-xs rounded-lg border font-medium transition-colors`}
                    style={form.priority === o.value
                        ? { background: PRIORITY_COLORS[o.value].bg, color: PRIORITY_COLORS[o.value].text, borderColor: 'currentColor' }
                        : { background: 'var(--t-input-bg)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Link to Lead (optional)</label>
              <select
                value={form.leadId || ''}
                onChange={(e) => setForm(f => ({ ...f, leadId: e.target.value || undefined }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="">No lead</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name} — {l.propertyAddress}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleSave} className="flex items-center gap-1 px-4 py-2 text-sm rounded-xl font-black uppercase tracking-widest transition-all"
              style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}
            >
              <CheckCircle2 size={14} /> {editingId ? 'Update Task' : 'Create Task'}
            </button>
            <button onClick={() => { setShowAdd(false); setEditingId(null); }} className="px-4 py-2 transition-all text-[10px] font-black uppercase tracking-widest rounded-xl" style={{ background: 'var(--t-surface-dim)', color: 'var(--t-text-muted)' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Task List View */}
      {viewMode === 'list' && (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden animate-in fade-in duration-200">
          {/* Header Row */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 text-[10px] font-black uppercase tracking-wider border-b mb-2 select-none" style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}>
            <div className="col-span-1"></div> {/* Checkbox and Star */}
            <button onClick={() => handleSortClick('title')} className="col-span-4 text-left flex items-center gap-1 hover:text-[var(--t-text)] font-black uppercase">
              Title {getSortIcon('title')}
            </button>
            <button onClick={() => handleSortClick('status')} className="col-span-2 text-left flex items-center gap-1 hover:text-[var(--t-text)] font-black uppercase">
              Status {getSortIcon('status')}
            </button>
            <button onClick={() => handleSortClick('due')} className="col-span-2 text-left flex items-center gap-1 hover:text-[var(--t-text)] font-black uppercase">
              Due Date {getSortIcon('due')}
            </button>
            <button onClick={() => handleSortClick('priority')} className="col-span-1 text-left flex items-center gap-1 hover:text-[var(--t-text)] font-black uppercase">
              Priority {getSortIcon('priority')}
            </button>
            <button onClick={() => handleSortClick('assignee')} className="col-span-1 text-left flex items-center gap-1 hover:text-[var(--t-text)] font-black uppercase">
              Assignee {getSortIcon('assignee')}
            </button>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4 custom-scrollbar">
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: 'var(--t-border)' }} />
                <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>No tasks found</p>
              </div>
            )}
            {filtered.map((task) => <TaskRow key={task.id} task={task} />)}
          </div>
        </div>
      )}

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="flex flex-col flex-1 min-h-0 space-y-4 animate-in fade-in duration-200">
          {/* Sorting Headers Row for Board View */}
          <div className="flex items-center gap-3 px-4 py-2 bg-[var(--t-surface)] rounded-2xl border border-[var(--t-border)] shadow-md select-none shrink-0">
            <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Sort Columns:</span>
            <div className="flex items-center gap-2 flex-wrap">
              <button 
                onClick={() => handleSortClick('title')} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${sortField === 'title' ? 'bg-[var(--t-primary)] text-[var(--t-on-primary)] shadow-sm' : 'bg-[var(--t-surface-dim)] hover:bg-[var(--t-surface-hover)]'}`}
                style={{ color: sortField === 'title' ? 'var(--t-on-primary)' : 'var(--t-text)' }}
              >
                Title {getSortIcon('title')}
              </button>
              <button 
                onClick={() => handleSortClick('status')} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${sortField === 'status' ? 'bg-[var(--t-primary)] text-[var(--t-on-primary)] shadow-sm' : 'bg-[var(--t-surface-dim)] hover:bg-[var(--t-surface-hover)]'}`}
                style={{ color: sortField === 'status' ? 'var(--t-on-primary)' : 'var(--t-text)' }}
              >
                Status {getSortIcon('status')}
              </button>
              <button 
                onClick={() => handleSortClick('due')} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${sortField === 'due' ? 'bg-[var(--t-primary)] text-[var(--t-on-primary)] shadow-sm' : 'bg-[var(--t-surface-dim)] hover:bg-[var(--t-surface-hover)]'}`}
                style={{ color: sortField === 'due' ? 'var(--t-on-primary)' : 'var(--t-text)' }}
              >
                Due Date {getSortIcon('due')}
              </button>
              <button 
                onClick={() => handleSortClick('priority')} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${sortField === 'priority' ? 'bg-[var(--t-primary)] text-[var(--t-on-primary)] shadow-sm' : 'bg-[var(--t-surface-dim)] hover:bg-[var(--t-surface-hover)]'}`}
                style={{ color: sortField === 'priority' ? 'var(--t-on-primary)' : 'var(--t-text)' }}
              >
                Priority {getSortIcon('priority')}
              </button>
              <button 
                onClick={() => handleSortClick('assignee')} 
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${sortField === 'assignee' ? 'bg-[var(--t-primary)] text-[var(--t-on-primary)] shadow-sm' : 'bg-[var(--t-surface-dim)] hover:bg-[var(--t-surface-hover)]'}`}
                style={{ color: sortField === 'assignee' ? 'var(--t-on-primary)' : 'var(--t-text)' }}
              >
                Assignee {getSortIcon('assignee')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0 overflow-hidden pb-4">
            {boardColumns.map(col => {
              const colTasks = filtered.filter(t => t.status === col.status);
              return (
                <div 
                  key={col.status} 
                  className="flex flex-col h-full bg-[var(--t-surface-dim)] rounded-xl border border-[var(--t-border)] overflow-hidden animate-in zoom-in-95 duration-200"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, col.status)}
                >
                  <div 
                    onClick={() => {
                      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                    }}
                    className={`flex items-center gap-2 p-3 border-b shrink-0 cursor-pointer select-none`} 
                    style={{ background: 'var(--t-surface)', borderColor: col.color.startsWith('border-[') ? col.color.match(/\[(.*?)\]/)![1] : col.color.split('-')[1] }}
                  >
                    <h3 className="text-sm font-semibold text-white flex-1 flex items-center gap-1">
                      {col.label} {getSortIcon(sortField)}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text-muted)' }}>{colTasks.length}</span>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
                    {colTasks.map(task => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, task.id)}
                        className="border rounded-xl p-3 transition-all cursor-grab active:cursor-grabbing hover:border-[var(--t-primary)]/50 astral-glass shrink-0"
                        style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
                        onClick={() => startEdit(task)}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority].dot}`} />
                            <span className={`text-[10px] font-bold ${PRIORITY_COLORS[task.priority].text}`}>
                              {task.priority.toUpperCase()}
                            </span>
                          </div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); toggleStarredTask(task.id); }}
                            className={`transition-colors ${starredTasks.includes(task.id) ? 'text-[var(--t-warning)]' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
                          >
                            <Star size={12} fill={starredTasks.includes(task.id) ? 'currentColor' : 'none'} />
                          </button>
                        </div>
                        <h4 className="text-sm font-semibold text-white mb-1 truncate">{task.title}</h4>
                        {task.description && (
                          <p className="text-xs line-clamp-2 mb-2" style={{ color: 'var(--t-text-muted)' }}>{task.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t" style={{ borderColor: 'var(--t-border)' }}>
                          <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                            <User size={10} /> {(getMemberName(task.assignedTo) || 'Unassigned').split(' ')[0]}
                          </span>
                          <span className={`inline-flex items-center gap-1 text-[10px] ${getDueColor(task)}`}>
                            <Clock size={10} /> {getDueLabel(task.dueDate)}
                          </span>
                        </div>
                      </div>
                    ))}
                    {colTasks.length === 0 && (
                      <div className="text-center py-6 text-xs border border-dashed rounded-xl shrink-0" style={{ background: 'rgba(0,0,0,0.1)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}>
                        No tasks
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {syncConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div 
            className="w-full max-w-md rounded-2xl p-6 border shadow-2xl space-y-6 transform animate-in zoom-in-95 duration-200"
            style={{ backgroundColor: 'var(--t-surface)', borderColor: 'var(--t-border)' }}
          >
            {syncConfirm.type === 'first-login' ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-400">
                    <Globe size={24} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[var(--t-text)]">Import Google Tasks?</h3>
                    <p className="text-xs text-[var(--t-text-muted)]">Connected Google Account detected.</p>
                  </div>
                </div>
                
                <p className="text-xs leading-relaxed text-[var(--t-text-muted)]">
                  Would you like to import all tasks from your Google Tasks list into WholeScale CRM?
                </p>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => syncConfirm.onChoose('import-yes')}
                    className="flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-white bg-[var(--t-primary)] hover:scale-[1.02] active:scale-95 transition-all"
                  >
                    Yes, Import
                  </button>
                  <button
                    onClick={() => syncConfirm.onChoose('import-no')}
                    className="flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest text-[var(--t-text)] bg-[var(--t-surface-dim)] border border-[var(--t-border)] hover:bg-[var(--t-surface)] transition-all"
                  >
                    No, Skip
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-yellow-500/10 text-yellow-400">
                    <AlertTriangle size={24} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[var(--t-text)]">Push Tasks to Google?</h3>
                    <p className="text-xs text-[var(--t-text-muted)]">CRM has local-only tasks.</p>
                  </div>
                </div>
                
                <p className="text-xs leading-relaxed text-[var(--t-text-muted)]">
                  You have <strong className="text-[var(--t-text)]">{syncConfirm.unlinkedCount}</strong> local task(s) that are not on Google.
                  {syncConfirm.oldCount > 0 && (
                    <span> This includes <strong className="text-[var(--t-text)]">{syncConfirm.oldCount}</strong> older task(s) (created more than 24 hours ago).</span>
                  )}
                  {" "}How would you like to handle them?
                </p>

                <div className="flex flex-col gap-2 pt-2">
                  <button
                    onClick={() => syncConfirm.onChoose('all')}
                    className="w-full py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-white bg-[var(--t-primary)] hover:scale-[1.01] active:scale-95 transition-all"
                  >
                    Push All Tasks
                  </button>
                  {syncConfirm.oldCount > 0 && (
                    <button
                      onClick={() => syncConfirm.onChoose('new-only')}
                      className="w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20 transition-all"
                    >
                      Push New Only ({syncConfirm.unlinkedCount - syncConfirm.oldCount})
                    </button>
                  )}
                  <button
                    onClick={() => syncConfirm.onChoose('none')}
                    className="w-full py-2.5 rounded-xl text-xs font-medium text-[var(--t-text-muted)] bg-[var(--t-surface-dim)] border border-[var(--t-border)] hover:bg-[var(--t-surface)] transition-all"
                  >
                    Pull Only (Don't Push CRM Tasks)
                  </button>
                  <button
                    onClick={() => syncConfirm.onChoose('cancel')}
                    className="w-full py-2 text-[10px] font-bold text-[var(--t-text-muted)] hover:text-[var(--t-error)] transition-all"
                  >
                    Cancel Sync
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
